/**
 * Phase H5b — SA cross-tenant assessment template library client.
 *
 * Wraps the BE surface added in `app/domains/assessment/admin_routes.py`:
 *
 *   GET  /super-admin/assessment-templates
 *          → cross-tenant library matrix
 *   GET  /super-admin/tenants/{tenant_slug}/assessment-templates
 *          → per-tenant SA scope (drives the AdmissionsPanel
 *            `template_slug` Select)
 *   POST /super-admin/assessment-templates/{template_id}/clone
 *          → copy a template into a target tenant (always lands `draft`)
 *
 * Iron Rule #1 — every endpoint here is BE-verified (see
 * eduviaos-backend PR #129 for the integration tests). Iron Rule #2 —
 * the request/response shapes mirror the BE 1:1 via `@eduviaos/shared`.
 *
 * The list reads default to `status=active` + `include_archived=false`
 * for the AdmissionsPanel Select (we want only deployable templates by
 * default) — callers that need the full matrix pass explicit filters.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import type {
  SuperAdminAssessmentTemplateCloneRequest,
  SuperAdminAssessmentTemplateCloneResponse,
  SuperAdminAssessmentTemplateListFilters,
  SuperAdminAssessmentTemplateListResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

const SA_TEMPLATES_ROOT = ["super-admin", "assessment-templates"] as const;

function buildListQuery(filters: SuperAdminAssessmentTemplateListFilters): string {
  const params = new URLSearchParams();
  if (filters.tenant_id) params.set("tenant_id", filters.tenant_id);
  if (filters.status) params.set("status", filters.status);
  if (filters.grade !== undefined) params.set("grade", String(filters.grade));
  if (filters.language) params.set("language", filters.language);
  if (filters.include_archived) params.set("include_archived", "true");
  if (filters.search) params.set("search", filters.search);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.skip !== undefined) params.set("skip", String(filters.skip));
  const q = params.toString();
  return q ? `?${q}` : "";
}

// ─── Cross-tenant library ─────────────────────────────────────────────

/** SA matrix view across every tenant. Used by the cross-tenant
 *  library + clone modal. Callers pass `tenant_id` to scope to one
 *  tenant; omit it for the full matrix. */
export function useSaAssessmentTemplatesCrossTenant(
  filters: SuperAdminAssessmentTemplateListFilters = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  return useQuery<SuperAdminAssessmentTemplateListResponse, ApiError>({
    queryKey: [...SA_TEMPLATES_ROOT, "cross-tenant", filters] as const,
    queryFn: () =>
      api.get<SuperAdminAssessmentTemplateListResponse>(
        `/super-admin/assessment-templates${buildListQuery(filters)}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

// ─── Per-tenant SA scope (feeds the AdmissionsPanel Select) ───────────

/** Templates owned by ONE tenant, fetched via the SA-scoped per-tenant
 *  alias so the `super_admin` JWT works (the IA-side `/assessments/...`
 *  routes reject cross-tenant identities). */
export function useSaAssessmentTemplatesForTenant(
  tenantSlug: string | null | undefined,
  filters: Omit<SuperAdminAssessmentTemplateListFilters, "tenant_id"> = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  return useQuery<SuperAdminAssessmentTemplateListResponse, ApiError>({
    queryKey: [...SA_TEMPLATES_ROOT, "per-tenant", tenantSlug, filters] as const,
    queryFn: () =>
      api.get<SuperAdminAssessmentTemplateListResponse>(
        `/super-admin/tenants/${encodeURIComponent(tenantSlug ?? "")}/assessment-templates${buildListQuery(filters)}`,
      ),
    enabled: enabled && Boolean(tenantSlug),
    staleTime: 30_000,
  });
}

// ─── Clone-to-tenant ──────────────────────────────────────────────────

export interface UseSaCloneAssessmentTemplateVars {
  template_id: string;
  body: SuperAdminAssessmentTemplateCloneRequest;
}

/** Copy a template into the target tenant as a draft. Invalidates the
 *  cross-tenant + per-tenant caches on success so any open SA matrix /
 *  Admissions selector reflects the new row immediately. */
export function useSaCloneAssessmentTemplate() {
  const qc = useQueryClient();
  return useMutation<
    SuperAdminAssessmentTemplateCloneResponse,
    ApiError,
    UseSaCloneAssessmentTemplateVars
  >({
    mutationKey: [...SA_TEMPLATES_ROOT, "clone"],
    mutationFn: ({ template_id, body }) =>
      api.post<SuperAdminAssessmentTemplateCloneResponse>(
        `/super-admin/assessment-templates/${encodeURIComponent(template_id)}/clone`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SA_TEMPLATES_ROOT });
    },
  });
}
